import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Thermometer, RotateCcw, Cpu, Activity } from 'lucide-react';
import { fleet } from '../api/client';
import { useMqtt } from '../hooks/useMqtt';
import { StatusDot } from '../components/StatusDot';
import { MetricCard } from '../components/MetricCard';
import { Skeleton } from '../components/Skeleton';
import { radToDeg } from '../lib/units';
import type { DeviceStatus } from '../types';

const JOINT_LABELS = ['J1 Base', 'J2 Shoulder', 'J3 Elbow', 'J4 Wrist 1', 'J5 Wrist 2', 'J6 Wrist 3'];

export default function DeviceDetailPage() {
  const { serial } = useParams<{ serial: string }>();
  const navigate = useNavigate();

  const { data: device, isLoading, error } = useQuery({
    queryKey: ['fleet', 'device', serial],
    queryFn: () => fleet.getDevice(serial!),
    enabled: !!serial,
    refetchInterval: 15_000,
  });

  const { isConnected, telemetry } = useMqtt(device ? [device] : []);
  const live = serial ? telemetry.get(serial) : undefined;

  if (isLoading) {
    return (
      <div>
        {/* Header skeleton */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-1.5"><ArrowLeft size={18} className="text-gray-600" /></div>
          <div className="flex-1">
            <Skeleton className="w-48 h-6 mb-2" />
            <Skeleton className="w-72 h-4" />
          </div>
        </div>

        {/* Metric cards skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="card">
              <Skeleton className="w-16 h-3 mb-2" />
              <Skeleton className="w-20 h-6 mb-1" />
              <Skeleton className="w-12 h-3" />
            </div>
          ))}
        </div>

        {/* Joint status skeleton */}
        <div className="card mb-6">
          <Skeleton className="w-24 h-4 mb-4" />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="bg-surface-2 rounded-lg p-3">
                <Skeleton className="w-20 h-3 mb-2" />
                <Skeleton className="w-16 h-6 mb-1" />
                <Skeleton className="w-12 h-3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400">Failed to load device details</p>
        <button onClick={() => navigate('/fleet')} className="btn-secondary mt-4 text-sm">
          Back to Fleet
        </button>
      </div>
    );
  }

  // Merge live telemetry with API response
  const deviceTelemetry = (device as unknown as Record<string, unknown>).telemetry as Record<string, unknown> | undefined;
  const jointPositions = live?.joint_positions ?? (deviceTelemetry?.joint_positions as number[]) ?? [];
  const jointTemps = live?.joint_temperatures ?? (deviceTelemetry?.joint_temperatures as number[]) ?? [];
  const cycleTimeMs = live?.cycle_time_ms ?? (deviceTelemetry?.cycle_time_ms as number) ?? 0;
  const pickRate = live?.pick_success_rate ?? (deviceTelemetry?.pick_success_rate as number) ?? 0;
  const cpuTemp = live?.cpu_temp ?? (deviceTelemetry?.cpu_temp as number) ?? 0;
  const powerWatts = live?.power_watts ?? (deviceTelemetry?.power_watts as number) ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/fleet')}
          className="p-1.5 rounded-lg hover:bg-surface-3 transition-colors"
        >
          <ArrowLeft size={18} className="text-gray-400" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-white">{device.name}</h2>
            <StatusDot status={device.status as DeviceStatus} showLabel size="md" />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {device.serial} &middot; {device.location} &middot; {device.org_name}
          </p>
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>Firmware v{device.firmware_version}</div>
          <div>Model v{device.model_version}</div>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Uptime"
          value={`${device.uptime_hours.toLocaleString()}h`}
          sub={isConnected ? 'Live' : undefined}
          accent="text-white"
        />
        <MetricCard
          label="Total Cycles"
          value={device.cycle_count.toLocaleString()}
          sub={cycleTimeMs ? `${cycleTimeMs}ms/cycle` : undefined}
          accent="text-white"
        />
        <MetricCard
          label="Pick Rate"
          value={pickRate ? `${pickRate}%` : '—'}
          accent={pickRate >= 99 ? 'text-emerald-400' : pickRate >= 95 ? 'text-amber-400' : 'text-red-400'}
        />
        <MetricCard
          label="Power"
          value={powerWatts ? `${powerWatts}W` : '—'}
          sub={cpuTemp ? `CPU ${cpuTemp}°C` : undefined}
          accent="text-white"
        />
      </div>

      {/* Joint status grid */}
      <div className="card mb-6">
        <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
          <Activity size={14} />
          Joint Status
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {JOINT_LABELS.map((label, i) => {
            const pos = jointPositions[i];
            const temp = jointTemps[i];
            return (
              <div key={label} className="bg-surface-2 rounded-lg p-3">
                <div className="text-[11px] text-gray-500 font-medium uppercase tracking-wide mb-2">
                  {label}
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-lg font-bold text-white font-mono">
                      {pos !== undefined ? `${radToDeg(pos).toFixed(1)}°` : '—'}
                    </div>
                    <div className="text-[11px] text-gray-500">Position</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-mono ${
                      temp !== undefined
                        ? temp > 55 ? 'text-red-400' : temp > 45 ? 'text-amber-400' : 'text-gray-300'
                        : 'text-gray-600'
                    }`}>
                      {temp !== undefined ? (
                        <span className="flex items-center gap-1">
                          <Thermometer size={12} />
                          {temp}°C
                        </span>
                      ) : '—'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* System info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <Cpu size={14} />
            System
          </h3>
          <dl className="space-y-2 text-sm">
            <Row label="Serial" value={device.serial} mono />
            <Row label="Firmware" value={`v${device.firmware_version}`} />
            <Row label="Model" value={`v${device.model_version}`} />
            <Row label="CPU Temp" value={cpuTemp ? `${cpuTemp}°C` : '—'} />
            <Row label="Power Draw" value={powerWatts ? `${powerWatts}W` : '—'} />
          </dl>
        </div>

        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
            <RotateCcw size={14} />
            Performance
          </h3>
          <dl className="space-y-2 text-sm">
            <Row label="Total Cycles" value={device.cycle_count.toLocaleString()} />
            <Row label="Cycle Time" value={cycleTimeMs ? `${cycleTimeMs}ms` : '—'} />
            <Row label="Pick Success" value={pickRate ? `${pickRate}%` : '—'} />
            <Row label="Uptime" value={`${device.uptime_hours.toLocaleString()} hours`} />
            <Row label="Last Seen" value={device.last_seen ? new Date(device.last_seen).toLocaleString() : '—'} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-gray-200 ${mono ? 'font-mono text-xs text-kinetica-400' : ''}`}>{value}</dd>
    </div>
  );
}
